/**
 * Moderation API
 *
 * Handles content reports, moderation actions, and moderation log.
 */

import type {
  ContentReport,
  ModerationLogEntry,
  ModerationActionType,
  ReportReason,
  ReportableContentType,
  ApiResponse,
  UUID,
  FeedItem,
} from '../types'
import { mockDb, delay } from './mockDb'
import { success, error } from './client'

const SESSION_KEY = 'docurious-session'

function getCurrentUserId(): string | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored).userId
  } catch {
    return null
  }
}

export const moderationApi = {
  /**
   * Submit a content report
   */
  async submitReport(data: {
    contentType: ReportableContentType
    contentId: string
    reason: ReportReason
    description?: string
    communityId?: string
  }): Promise<ApiResponse<ContentReport>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    const now = new Date().toISOString()

    // Get content preview
    let contentPreview = ''
    if (data.contentType === 'feed_post') {
      const post = mockDb.findById<FeedItem>('feedItems', data.contentId)
      contentPreview = post?.textContent?.slice(0, 100) ?? 'Feed post'
    }

    const report: ContentReport = {
      id: `report-${Date.now()}`,
      reporterId: userId,
      contentType: data.contentType,
      contentId: data.contentId,
      reason: data.reason,
      description: data.description,
      communityId: data.communityId,
      status: 'pending',
      contentPreview,
      createdAt: now,
      updatedAt: now,
    }

    mockDb.insertWithId('reports' as any, report)

    return success(report)
  },

  /**
   * Get reports for a community (moderator only)
   */
  async getCommunityReports(
    communityId: string,
    status?: string
  ): Promise<ApiResponse<ContentReport[]>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    let reports = mockDb.findWhere<ContentReport>(
      'reports' as any,
      (r) => r.communityId === communityId
    )

    if (status) {
      reports = reports.filter((r) => r.status === status)
    }

    // Populate reporter info
    reports = reports.map((r) => {
      const reporter = mockDb.findById<any>('users', r.reporterId)
      return {
        ...r,
        reporter: reporter
          ? { id: reporter.id, displayName: reporter.displayName }
          : undefined,
      }
    })

    // Sort by newest first
    reports.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

    return success(reports)
  },

  /**
   * Review a report (moderator action)
   */
  async reviewReport(
    reportId: string,
    decision: {
      status: 'action_taken' | 'dismissed'
      actionTaken?: ModerationActionType
      note?: string
    }
  ): Promise<ApiResponse<ContentReport>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    const report = mockDb.findById<ContentReport>('reports' as any, reportId)
    if (!report) return error('Report not found')

    const updated = mockDb.update<ContentReport>('reports' as any, reportId, {
      status: decision.status,
      actionTaken: decision.actionTaken,
      reviewedByUserId: userId,
      reviewedAt: new Date().toISOString(),
      reviewNote: decision.note,
    })

    if (!updated) return error('Failed to update report')

    // If action taken, log it
    if (decision.actionTaken && report.communityId) {
      const logEntry: ModerationLogEntry = {
        id: `modlog-${Date.now()}`,
        communityId: report.communityId,
        moderatorUserId: userId,
        targetContentId: report.contentId,
        targetContentType: report.contentType,
        action: decision.actionTaken,
        reason: report.reason,
        note: decision.note,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      mockDb.insertWithId('moderationLog' as any, logEntry)
    }

    return success(updated)
  },

  /**
   * Mute a member in a community
   */
  async muteMember(
    communityId: string,
    targetUserId: string,
    reason?: string
  ): Promise<ApiResponse<void>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    // Find the membership
    const membership = mockDb.findOne<any>(
      'communityMemberships',
      (m: any) => m.communityId === communityId && m.userId === targetUserId
    )
    if (!membership) return error('Member not found')

    // Update status to muted
    mockDb.update('communityMemberships', membership.id, {
      status: 'muted',
      mutedAt: new Date().toISOString(),
    })

    // Log the action
    const logEntry: ModerationLogEntry = {
      id: `modlog-${Date.now()}`,
      communityId,
      moderatorUserId: userId,
      targetUserId,
      action: 'user_muted',
      reason,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    mockDb.insertWithId('moderationLog' as any, logEntry)

    return success(undefined)
  },

  /**
   * Unmute a member
   */
  async unmuteMember(
    communityId: string,
    targetUserId: string
  ): Promise<ApiResponse<void>> {
    await delay(100)

    const membership = mockDb.findOne<any>(
      'communityMemberships',
      (m: any) => m.communityId === communityId && m.userId === targetUserId
    )
    if (!membership) return error('Member not found')

    mockDb.update('communityMemberships', membership.id, {
      status: 'active',
      mutedAt: undefined,
    })

    return success(undefined)
  },

  /**
   * Get moderation log for a community
   */
  async getModerationLog(
    communityId: string
  ): Promise<ApiResponse<ModerationLogEntry[]>> {
    await delay(50)

    let entries = mockDb.findWhere<ModerationLogEntry>(
      'moderationLog' as any,
      (e) => e.communityId === communityId
    )

    // Populate moderator and target user info
    entries = entries.map((entry) => {
      const moderator = mockDb.findById<any>('users', entry.moderatorUserId)
      const targetUser = entry.targetUserId
        ? mockDb.findById<any>('users', entry.targetUserId)
        : null
      return {
        ...entry,
        moderator: moderator
          ? { id: moderator.id, displayName: moderator.displayName }
          : undefined,
        targetUser: targetUser
          ? { id: targetUser.id, displayName: targetUser.displayName }
          : undefined,
      }
    })

    entries.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

    return success(entries)
  },
}
